import {z} from 'zod';

/**
 * Schema for ExerciseWorkout - individual exercise within a workout block
 * Validates: Requirements 2.1, 2.4
 */
export const exerciseWorkoutSchema = z.object({
  id: z.union([z.string(), z.number()]).nullish(),
  tempId: z.union([z.string(), z.number()]).nullish(),
  exerciseId: z.union([z.string(), z.number()]),
  exercise: z
    .object({
      value: z.union([z.string(), z.number()]),
      label: z.string(),
    })
    .nullish(),
  repetitions: z.string().nullish(),
  notes: z.string().nullish(),
  rounds: z.string().nullish(),
  load: z.string().nullish(),
  time: z.union([z.string(), z.number()]).nullish(),
  timeText: z.string().nullish(),
  restTime: z.union([z.string(), z.number()]).nullish(),
  order: z.number().nullish(),
  _destroy: z.boolean().nullish(),
});

/**
 * Schema for Workout - a block within a workout group (e.g., "Warmup", "WOD", "Strength")
 * Validates: Requirements 2.1, 2.4
 */
export const workoutSchema = z.object({
  id: z.union([z.string(), z.number()]).nullish(),
  tempId: z.union([z.string(), z.number()]).nullish(),
  name: z.string().min(1, 'Nome do bloco é obrigatório'),
  description: z.string().nullish(),
  time: z.union([z.string(), z.number()]).nullish(),
  time_text: z.string().nullish(),
  checkinEnabled: z.boolean().nullish().default(false),
  exerciseWorkouts: z.array(exerciseWorkoutSchema).default([]),
  order: z.number().nullish(),
  _destroy: z.boolean().nullish(),
});

/**
 * Schema for WorkoutGroup - a training session (e.g., "Treino A", "Treino B")
 * Validates: Requirements 2.1, 2.4
 */
export const workoutGroupSchema = z.object({
  id: z.union([z.string(), z.number()]).nullish(),
  tempId: z.union([z.string(), z.number()]).nullish(),
  name: z
    .string()
    .min(1, 'Nome do treino é obrigatório')
    .default('Seu novo treino'),
  description: z.string().nullish(),
  workouts: z.array(workoutSchema).default([]),
  coverUrl: z.any().nullish(),
  videoUrl: z.any().nullish(),
  audioUrl: z.any().nullish(),
  attachmentUrl: z.any().nullish(),
  cover: z.any().nullish(),
  video: z.any().nullish(),
  audio: z.any().nullish(),
  attachment: z.any().nullish(),
  order: z.number().nullish(),
  _destroy: z.boolean().nullish(),
});

/**
 * Schema for WorkoutRoutine - the root entity containing all workout groups
 * Validates: Requirements 2.1, 2.4
 */
export const workoutRoutineSchema = z.object({
  id: z.union([z.string(), z.number()]).nullish(),
  name: z.string().min(1, 'Nome da rotina é obrigatório'),
  startDate: z.string().min(1, 'Data de início é obrigatória').nullish(),
  endDate: z.string().min(1, 'Data de término é obrigatória').nullish(),
  cycleType: z.enum(['user', 'date']).nullish().default('date'),
  isDraft: z.boolean().nullish(),
  showOnlyOnScheduledDays: z.boolean().nullish(),
  target: z.enum(['programs', 'users']).nullish().default('programs'),
  commentEnable: z.boolean().nullish().default(true),
  programIds: z.array(z.union([z.string(), z.number()])).default([]),
  programsSelected: z
    .array(
      z.object({
        value: z.union([z.string(), z.number()]),
        label: z.string(),
      }),
    )
    .default([]),
  usersTargetIds: z.array(z.union([z.string(), z.number()])).default([]),
  usersTarget: z.array(z.any()).default([]),
  weekDayIds: z.array(z.union([z.string(), z.number()])).default([]),
  showDaysOfWeek: z.boolean().nullish(),
  workoutGroups: z.array(workoutGroupSchema).default([]),
});

// Inferred TypeScript types from Zod schemas
// Using z.output to get the type after parsing (with defaults applied)
export type ExerciseWorkoutFormData = z.output<typeof exerciseWorkoutSchema>;
export type WorkoutFormData = z.output<typeof workoutSchema>;
export type WorkoutGroupFormData = z.output<typeof workoutGroupSchema>;
export type WorkoutRoutineFormData = z.output<typeof workoutRoutineSchema>;

// Input types for form data before parsing (with optional fields)
export type ExerciseWorkoutInput = z.input<typeof exerciseWorkoutSchema>;
export type WorkoutInput = z.input<typeof workoutSchema>;
export type WorkoutGroupInput = z.input<typeof workoutGroupSchema>;
export type WorkoutRoutineInput = z.input<typeof workoutRoutineSchema>;

// Helper function to generate unique temp IDs
export const generateTempId = (): string => {
  return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Default values for creating new items
export const createDefaultExerciseWorkout = (): ExerciseWorkoutFormData => ({
  tempId: generateTempId(),
  exerciseId: '',
  repetitions: '',
  notes: '',
  rounds: '',
  load: '',
  time: '',
  restTime: '',
});

export const createDefaultWorkout = (): WorkoutFormData => ({
  tempId: generateTempId(),
  name: '',
  description: '',
  time_text: '',
  checkinEnabled: false,
  exerciseWorkouts: [],
});

export const createDefaultWorkoutGroup = (): WorkoutGroupFormData => ({
  tempId: generateTempId(),
  name: 'Seu novo treino',
  description: '',
  workouts: [
    {
      tempId: generateTempId(),
      name: 'Seu novo treino',
      description: '',
      time_text: '',
      checkinEnabled: false,
      exerciseWorkouts: [],
    },
  ],
});

// Validation helper to check if an item is new (has tempId but no id)
export const isNewItem = (item: {
  id?: string | number;
  tempId?: string | number;
}): boolean => {
  return !item.id && !!item.tempId;
};

// Validation helper to check if an item is persisted (has id)
export const isPersistedItem = (item: {id?: string | number}): boolean => {
  return !!item.id;
};
